import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

import { SubscriptionPaymentModal } from "@/components/SubscriptionPaymentModal";
import { Crown, Zap, Check, X, Coins } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "@/hooks/useTranslation";
import apiClient from "@/api/ApiClient";
import realisticRomantic from "@/assets/realistic-romantic.jpg";

interface PremiumProps {
  onNavigateToChat: () => void;
  onNavigateToMyBoyfriends: () => void;
  onNavigateToProfile: () => void;
  onNavigateToSettings: () => void;
  onNavigateToCharacterCreation: () => void;
  onNavigateToExplore?: () => void;
  onNavigateToPremium?: () => void;
  user?: {
    email?: string;
    name?: string;
    isPremium?: boolean;
  } | null;
  onPromptLogin?: () => void;
  onTokensClick?: () => void;
  onUpgradeToPremium?: () => void;
  userTokens?: number;
  source?: 'onboarding' | 'upgrade' | 'chat';
  onGoBack?: () => void;
  selectedBoyfriendPhoto?: string;
}

export const Premium = ({ 
  onNavigateToChat, 
  onNavigateToMyBoyfriends, 
  onNavigateToProfile, 
  onNavigateToSettings, 
  onNavigateToCharacterCreation,
  onNavigateToExplore,
  onNavigateToPremium,
  user,
  onPromptLogin,
  onTokensClick,
  onUpgradeToPremium,
  userTokens,
  source = 'onboarding',
  onGoBack,
  selectedBoyfriendPhoto
}: PremiumProps) => {
  const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'yearly'>('monthly');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [premiumFreeTokens, setPremiumFreeTokens] = useState<number | null>(null);
  const { toast } = useToast();
  const { t } = useTranslation();

  // Fetch premium free tokens from API
  useEffect(() => {
    const fetchPremiumConfig = async () => {
      try {
        const response = await apiClient.getSubscriptionPlans();
        if (response?.premiumFreeTokens) {
          setPremiumFreeTokens(response.premiumFreeTokens);
        }
      } catch (error) {
        console.error('Error fetching premium free tokens:', error);
      }
    };
    fetchPremiumConfig();
  }, []);

  const handlePurchaseSubscription = (plan: 'monthly' | 'yearly', paymentMethod: string) => {
    if (onUpgradeToPremium) {
      onUpgradeToPremium();
    }
    // Silent upgrade - no notification
  };

  const handleUpgradeClick = () => {
    if (!user) {
      // Show auth modal for unsigned users
      if (onPromptLogin) {
        onPromptLogin();
      }
    } else {
      // Show payment modal for signed users
      setShowPaymentModal(true);
    }
  };

  return (
    <div className="bg-gradient-dreamy h-screen animate-fade-in overflow-hidden flex flex-col">
      {/* Hero Image Section - Full Width Mobile */}
      <div className="relative h-[50vh] md:h-[520px] md:mx-6 md:mt-6 md:rounded-xl overflow-hidden shrink-0">
        <img 
          src={selectedBoyfriendPhoto || realisticRomantic} 
          alt="Premium boyfriend"
          className="w-full h-full object-cover"
        />
        <div className="absolute bottom-0 left-0 right-0 h-1/2 bg-gradient-to-t from-black via-black/40 to-transparent" />
        
        {/* Floating Text */}
        <div className="absolute bottom-3 left-4 right-4 z-10">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-base md:text-xl font-light text-white/90 mb-0.5">
                {t('premium_modal.unlock_the')}
              </p>
              <h1 className="text-3xl md:text-5xl font-bold text-white leading-none">
                {t('premium_modal.premium')}
              </h1>
              <h2 className="text-xl md:text-3xl font-light text-primary-glow">
                {t('premium_modal.experience')}
              </h2>
            </div>
            <Crown className="h-10 w-10 md:h-16 md:w-16 text-amber-400" />
          </div>
        </div>

        {/* Close button */}
        <button
          onClick={() => {
            if (onGoBack) {
              onGoBack();
            } else if (source === 'chat') {
              onNavigateToChat();
            } else if (onNavigateToExplore) {
              onNavigateToExplore();
            }
          }}
          className="absolute right-4 z-20 p-2 rounded-full bg-black/20 hover:bg-black/40 text-white transition-colors"
          style={{ top: `calc(1rem + var(--safe-area-inset-top, 0px))` }}
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Main Content */}
      <div
        className="px-5 pt-3 relative flex-1 flex flex-col min-h-0"
        style={{ paddingBottom: `calc(1.5rem + var(--safe-area-inset-bottom, 0px))` }}
      >
        <div className="max-w-2xl mx-auto w-full flex flex-col flex-1 min-h-0">

          <div className="space-y-3 flex flex-col flex-1 min-h-0">

            {/* Features Section */}
            <div className="grid grid-cols-2 gap-2 text-base">
              <div className="space-y-2">
                <div className="flex items-center gap-2 p-2.5 rounded-lg bg-gradient-to-r from-primary/10 to-primary-glow/10">
                  <span className="text-lg shrink-0">💬</span>
                  <span className="text-sm font-medium text-white">{t('premium_modal.unlimited_chats')}</span>
                </div>
                <div className="flex items-center gap-2 p-2.5 rounded-lg bg-gradient-to-r from-primary/10 to-primary-glow/10">
                  <span className="text-lg shrink-0">👑</span>
                  <span className="text-sm font-medium text-white">{t('premium_modal.premium_bfs')}</span>
                </div>
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center gap-2 p-2.5 rounded-lg bg-gradient-to-r from-primary/10 to-primary-glow/10">
                  <span className="text-lg shrink-0">🔥</span>
                  <span className="text-sm font-medium text-white">{t('premium_modal.private_photos')}</span>
                </div>
                <div className="flex items-center gap-2 p-2.5 rounded-lg bg-gradient-to-r from-primary/10 to-primary-glow/10">
                  <Coins className="h-5 w-5 shrink-0 text-amber-400" />
                  <span className="text-sm font-medium text-white">
                    {premiumFreeTokens !== null ? `${premiumFreeTokens} ${t('premium_modal.free_tokens_text')}` : t('premium_modal.free_tokens')}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Pricing Plans */}
            <div className="grid grid-cols-2 gap-2">
              <Card 
                className={`p-2 text-center border-2 cursor-pointer transition-all hover:scale-105 ${
                  selectedPlan === 'monthly' 
                    ? 'border-primary bg-primary/10 ring-2 ring-primary/20 shadow-lg' 
                    : 'border-primary/20 hover:border-primary/40'
                }`}
                onClick={() => setSelectedPlan('monthly')}
              >
                <h3 className="text-base font-bold mb-1">{t('premium_modal.monthly')}</h3>
                <div className="mb-1">
                  <p className="text-[10px] text-muted-foreground line-through">$19.99</p>
                  <p className="text-xl font-bold text-primary">$9.99</p>
                  <p className="text-[10px] text-muted-foreground">{t('premium_modal.per_month')}</p>
                  <p className="text-[10px] text-green-500 font-medium mt-1">{t('premium_modal.save_50')}</p>
                </div>
              </Card>

              <Card 
                className={`p-2 text-center border-2 cursor-pointer transition-all hover:scale-105 relative ${
                  selectedPlan === 'yearly' 
                    ? 'border-primary bg-primary/10 ring-2 ring-primary/20 shadow-lg' 
                    : 'border-primary/20 hover:border-primary/40'
                }`}
                onClick={() => setSelectedPlan('yearly')}
              >
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-white text-pink-500 px-2 py-0.5 rounded-full text-[10px] font-bold whitespace-nowrap">
                    {t('premium_modal.save_50')}
                  </span>
                </div>
                <h3 className="text-base font-bold mb-1">{t('premium_modal.yearly')}</h3>
                <div className="mb-1">
                  <p className="text-[10px] text-muted-foreground line-through">$159.99</p>
                  <p className="text-xl font-bold text-primary">$79.99</p>
                  <p className="text-[10px] text-muted-foreground">{t('premium_modal.per_year')}</p>
                  <p className="text-[10px] text-green-500 font-medium mt-1">{t('premium_modal.save_50')}</p>
                </div>
              </Card>
            </div>
            
            <div className="flex flex-col gap-1.5 mt-auto">
              <Button 
                variant="romantic" 
                size="lg" 
                className="w-full py-3 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105"
                onClick={handleUpgradeClick}
              >
                <Crown className="h-4 w-4 mr-2" />
                {t('premium_modal.upgrade_to_plan', { plan: selectedPlan === 'monthly' ? t('premium_modal.monthly_plan') : t('premium_modal.yearly_plan') })}
              </Button>
              <p className="text-[10px] text-muted-foreground text-center">
                {t('premium_modal.secure_payment_cancel')}
              </p>
            </div>
          </div>
        </div>

        {/* Payment Modal */}
        <SubscriptionPaymentModal
          open={showPaymentModal}
          onOpenChange={setShowPaymentModal}
          selectedPlan={selectedPlan}
          onPurchaseSubscription={handlePurchaseSubscription}
        />

        {/* Fake Delete Confirmation Dialog */}
        <Dialog open={showDeleteConfirmation} onOpenChange={setShowDeleteConfirmation}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-red-600">Delete everything?</DialogTitle>
              <DialogDescription className="text-center space-y-4">
                <p>Are you sure you want to delete all your progress?</p>
                <p className="text-sm font-medium text-amber-600">
                  💡 Keep your memories safe with Premium - unlimited storage and backup!
                </p>
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-2">
              <Button 
                variant="romantic" 
                className="w-full"
                onClick={() => {
                  setShowDeleteConfirmation(false);
                }}
              >
                <Crown className="h-4 w-4 mr-2" />
                Keep Everything with Premium
              </Button>
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => {
                  setShowDeleteConfirmation(false);
                  if (onNavigateToExplore) {
                    onNavigateToExplore();
                  }
                }}
              >
                Delete everything
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};